import { useEffect, useState } from 'react'
import { Milestone, AlertTriangle, Presentation, Search } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { EmptyState } from '@/components/ui/empty-state'
import { ListSkeleton } from '@/components/ui/skeleton'
import { useTrackerTickets } from '@/queries/tracker'
import { useTicketCompanies } from '@/queries/tickets'
import { useIsAdmin } from '@/lib/auth'
import { formatTicketRef } from '@/lib/ticketRef'
import { ViewingAsBanner } from '@/features/view-as/ViewingAsBanner'
import {
  trackerStatusLabels,
  trackerStatusVariants,
  devStageLabels,
  devStageVariants,
  priorityLabels,
} from '@/lib/status'
import type { TrackerStatus, TrackerTicket } from '@/types/tracker'
import type { TicketPriority } from '@/types/ticket'

const TRACKER_STATUSES: Array<TrackerStatus | 'all'> = [
  'all',
  'new',
  'needs_info',
  'accepted',
  'proposed_closed',
  'closed',
  'on_hold',
  'wont_do',
]

const PRIORITIES: Array<TicketPriority | 'all'> = ['all', 'high', 'normal', 'low']

function TrackerRow({
  ticket,
  asUser,
  companyName,
  presentSearch,
}: {
  ticket: TrackerTicket
  asUser?: string
  /** Present only for the admin cross-company view — renders a company column. */
  companyName?: string
  /** The list's current status/priority/q filters, carried into the Present link — see TrackerListView. */
  presentSearch: { status?: TrackerStatus; priority?: TicketPriority; q?: string }
}) {
  const { tracker } = ticket
  // Staff-only, like TrackerControls' "Track this issue" — checked here so
  // the affordance is verifiably absent for a client regardless of who ends
  // up rendering this row. Clients don't present.
  const isAdmin = useIsAdmin()

  return (
    <div className="group flex h-11 items-center gap-1 px-2 text-etn-control transition-colors hover:bg-etn-row-hover">
      <Link
        // The detail route arrives in a later task — link to the path now so
        // rows are already wired up once it exists.
        to={`/tracker/${ticket._id}` as never}
        search={{ asUser } as never}
        className="flex min-w-0 flex-1 items-center gap-3"
      >
        <span className="w-16 shrink-0 text-etn-micro text-etn-text-soft tabular-nums">{formatTicketRef(ticket)}</span>
        {companyName !== undefined && (
          <span className="w-32 shrink-0 truncate text-etn-micro text-etn-text-soft">{companyName}</span>
        )}
        <div className="flex-1 min-w-0">
          <span className="block truncate font-semibold text-etn-text">{ticket.subject}</span>
          {tracker.category && (
            <span className="block truncate text-etn-micro text-etn-text-soft">{tracker.category}</span>
          )}
        </div>
        <Badge variant={trackerStatusVariants[tracker.status]}>{trackerStatusLabels[tracker.status]}</Badge>
        <Badge variant={devStageVariants[tracker.devStage]}>{devStageLabels[tracker.devStage]}</Badge>
        <span className="w-14 shrink-0 text-right text-etn-micro text-etn-text-soft">{priorityLabels[ticket.priority]}</span>
        <span className="w-24 shrink-0 text-right text-etn-micro text-etn-text-soft tabular-nums">
          {tracker.expectedDate ? dayjs(tracker.expectedDate.slice(0, 10)).format('D MMM YYYY') : ''}
        </span>
      </Link>
      {isAdmin && (
        // The list mixes every company's tickets for an unlensed admin (see
        // buildTrackerScope) — this row's own companyId is the only reliable
        // way to know which company's deck to open, lensed or not. The list's
        // active status/priority/q filters ride along too (design doc
        // "Presenting follows the list's filters", 2026-08-24) so "filter to
        // high priority, then present" shows exactly those issues.
        <Link
          to="/tracker/present"
          search={{ companyId: ticket.companyId, asUser, ...presentSearch } as never}
          aria-label="Present"
          title="Present"
          className="shrink-0 rounded-etn-menu p-1.5 text-etn-text-soft opacity-0 transition-opacity hover:bg-etn-surface hover:text-etn-text group-hover:opacity-100 focus-visible:opacity-100"
        >
          <Presentation className="h-4 w-4" />
        </Link>
      )}
    </div>
  )
}

export function TrackerListView({ asUser, admin = false }: { asUser?: string; admin?: boolean }) {
  const [status, setStatus] = useState<TrackerStatus | 'all'>('all')
  const [priority, setPriority] = useState<TicketPriority | 'all'>('all')
  const [companyId, setCompanyId] = useState('')

  // Debounced search, same shape as AdminQueueView's: local input state, and
  // an applied value that drives both the request and the Present link so
  // the two never disagree about what's actually been searched for.
  const [searchText, setSearchText] = useState('')
  const [q, setQ] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setQ(searchText.trim()), 300)
    return () => clearTimeout(t)
  }, [searchText])

  const params: Record<string, unknown> = {}
  if (status !== 'all') params.status = status
  if (priority !== 'all') params.priority = priority
  if (q) params.q = q
  if (admin) {
    // companyId is honoured server-side for an admin (supportTicket.service's
    // listTrackerTickets) — sent as a real query param, not filtered
    // client-side, so it narrows the SAME 25-row default page rather than
    // slicing an already-truncated one. Also request the server's max page:
    // an unlensed admin's list spans every company combined, so it can
    // exceed the default page long before any single company's filter would.
    params.limit = 100
    if (companyId) params.companyId = companyId
  }

  const { data, isLoading, error, refetch } = useTrackerTickets(params, asUser)
  // Rows carry only companyId (see listTrackerTickets) — no companyName join
  // server-side — so the company column's display name still comes from the
  // same company list the admin ticket queue's filter uses.
  const companies = useTicketCompanies(admin)
  const companyNames = new Map((companies.data?.data ?? []).map(c => [c._id, c.name ?? c._id]))

  const rows = data?.data ?? []
  // True once the server-side page itself is short of the real total — can't
  // happen for a non-admin (their own scope never approaches 100 promoted
  // tickets) but is exactly the case a bare company-name join over a partial
  // page would otherwise hide silently.
  const truncated = admin && (data?.total ?? 0) > rows.length

  // Carried into each row's Present link (design doc "Presenting follows the
  // list's filters", 2026-08-24) — built once here, not per-row, so every
  // row's deck link agrees on exactly what's currently applied to the list.
  const presentSearch: { status?: TrackerStatus; priority?: TicketPriority; q?: string } = {}
  if (status !== 'all') presentSearch.status = status
  if (priority !== 'all') presentSearch.priority = priority
  if (q) presentSearch.q = q

  return (
    <>
      {data?.viewingAs && <ViewingAsBanner name={data.viewingAs.name} companyName={data.viewingAs.companyName} />}
      <div className="flex gap-8">
        <div className="flex-1 min-w-0">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h1 className="text-lg font-semibold text-etn-text">Tracker</h1>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-etn-text-soft" />
                <Input
                  pill
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  placeholder="Search subjects…"
                  className="w-48 pl-9"
                />
              </div>
              {admin && (
                <select
                  aria-label="Company"
                  className="h-[var(--etn-control-height-input)] rounded-etn-menu border border-etn-border-input bg-etn-surface px-3 text-etn-control text-etn-text-cell max-w-44"
                  value={companyId}
                  onChange={e => setCompanyId(e.target.value)}
                >
                  <option value="">All companies</option>
                  {companies.data?.data.map(c => (
                    <option key={c._id} value={c._id}>{c.name ?? c._id}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="mb-2 flex flex-wrap gap-1.5">
            {TRACKER_STATUSES.map(s => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                className={`rounded-etn-pill border px-2.5 py-1 text-etn-control-sm font-semibold transition-colors ${
                  status === s
                    ? 'border-transparent bg-primary text-primary-foreground'
                    : 'border-etn-border-hairline text-etn-text-cell hover:bg-etn-control-subtle'
                }`}
              >
                {s === 'all' ? 'All' : trackerStatusLabels[s]}
              </button>
            ))}
          </div>

          {/* Same pill idiom as the status filter above (design doc
              "Presenting follows the list's filters", 2026-08-24) — priority
              didn't have a list-side filter before this. */}
          <div className="mb-4 flex flex-wrap gap-1.5">
            {PRIORITIES.map(p => (
              <button
                key={p}
                type="button"
                onClick={() => setPriority(p)}
                className={`rounded-etn-pill border px-2.5 py-1 text-etn-control-sm font-semibold transition-colors ${
                  priority === p
                    ? 'border-transparent bg-primary text-primary-foreground'
                    : 'border-etn-border-hairline text-etn-text-cell hover:bg-etn-control-subtle'
                }`}
              >
                {p === 'all' ? 'All priorities' : priorityLabels[p]}
              </button>
            ))}
          </div>

          {isLoading && <ListSkeleton rows={5} />}

          {error && (
            <div className="flex flex-col items-center gap-3 py-16">
              <EmptyState
                icon={<AlertTriangle />}
                title="Couldn't load the tracker."
                body="Something went wrong. Please try again."
              />
              <Button size="sm" variant="outline" onClick={() => refetch()}>Retry</Button>
            </div>
          )}

          {!isLoading && !error && rows.length === 0 && (
            <EmptyState
              icon={<Milestone />}
              title="No promoted tickets yet"
              body="When we promote a ticket to the tracker it'll show up here."
            />
          )}

          {!isLoading && !error && rows.length > 0 && (
            <>
              {truncated && (
                <p className="mb-2 text-etn-micro text-etn-text-soft">
                  Showing first {rows.length} of {data!.total} — narrow with the company filter or a status to see the rest.
                </p>
              )}
              <div className="divide-y divide-etn-border-row rounded-etn-card border border-etn-border-hairline bg-etn-surface shadow-etn-whisper">
                {rows.map((t: TrackerTicket) => (
                  <TrackerRow
                    key={t._id}
                    ticket={t}
                    asUser={asUser}
                    companyName={admin ? companyNames.get(t.companyId) ?? 'Unknown company' : undefined}
                    presentSearch={presentSearch}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
